// Text formatting helpers — title-cases statuses while preserving common acronyms.

const ACRONYMS = new Set([
  "SLA", "KPI", "AR", "AP", "MTD", "QTD", "YTD", "FY", "FX",
  "AED", "USD", "EUR", "INR", "SAR", "QAR", "KWD", "VAT",
  "TRN", "MOIAT", "ESMA", "ISO", "PDF", "PDFs", "API", "URL",
  "CRM", "ERP", "GRN", "PO", "RFQ", "BU", "BUs", "TAT", "NDA",
  "QA", "QC", "RBAC", "PII", "DR", "TLS", "SSL", "OTP", "TOTP",
  "ID", "IP", "DNS", "GST", "SSO", "IT", "HR", "BR", "BD", "UAE",
  "MQL", "SQL", "TLS", "TOS", "VPN", "WAF", "JWT",
]);

const SMALL_WORDS = new Set(["a", "an", "and", "as", "at", "but", "by", "for",
                             "in", "of", "on", "or", "the", "to", "vs", "via",
                             "with"]);

function _wordCase(word, isFirst) {
  if (!word) return word;
  const upper = word.toUpperCase();
  if (ACRONYMS.has(upper)) return upper;
  // If word contains digits or mixed case markers, preserve as-is
  if (/\d/.test(word)) return upper.toLowerCase().split("").map((c, i) => i === 0 ? c.toUpperCase() : c).join("");
  const lower = word.toLowerCase();
  if (!isFirst && SMALL_WORDS.has(lower)) return lower;
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

/**
 * Convert SNAKE_CASE / kebab-case / SHOUTING text into clean Title Case.
 * Preserves acronyms (SLA, KPI, AR, MTD, AED, VAT, MOIAT, ESMA, ISO, PDF, etc.).
 */
export function toTitleLabel(value) {
  if (value == null) return "";
  const s = String(value).trim();
  if (!s) return "";
  return s
    .replace(/[_\-/]+/g, " ")
    .replace(/\s+/g, " ")
    .split(" ")
    .map((w, i) => _wordCase(w, i === 0))
    .join(" ");
}

/** Identical to toTitleLabel but with a status-friendly null fallback ("—"). */
export function formatStatusLabel(value) {
  const out = toTitleLabel(value);
  return out || "—";
}

/** Metric eyebrow labels — uppercase to match KPI tile design. */
export function formatMetricLabel(value) {
  return (value || "").toString().replace(/[_\-/]+/g, " ").toUpperCase();
}

/** Lower-case display value for free-form fields (keeps acronyms). */
export function formatSentence(value) {
  if (value == null) return "";
  return String(value).replace(/[_\-/]+/g, " ").replace(/\s+/g, " ").trim();
}


/** Map known internal codes → friendly UI text. Used for satisfaction reasons,
 *  short blocker labels, etc. */
const FRIENDLY_CODE_MAP = {
  NO_DOCUMENT: "No file uploaded",
  AWAITING_APPROVAL: "Awaiting approval",
  AWAITING_REVIEW: "Awaiting review",
  REJECTED: "Rejected",
  EXPIRED: "Expired",
  EXPIRING_SOON: "Expiring soon",
  NOT_APPLICABLE: "Not applicable",
  WAIVED: "Waived",
  APPROVED: "Approved",
  UPLOADED: "Uploaded",
  REQUIRED: "Required",
};

/** Humanise short status / blocker codes for UI display only. */
export function friendlyCode(value) {
  if (value == null) return "";
  const s = String(value).trim();
  if (!s) return "";
  if (FRIENDLY_CODE_MAP[s]) return FRIENDLY_CODE_MAP[s];
  return toTitleLabel(s);
}

/** Convert noisy ALL_CAPS_UNDERSCORED business names into readable display
 *  text — used for customer names, prospect names etc. when the underlying
 *  stored value still carries legacy formatting. */
export function displayName(value) {
  if (value == null) return "";
  const s = String(value).trim();
  if (!s) return "";
  // If the string already has lowercase letters AND mixed case, treat as-is.
  if (/[a-z]/.test(s) && /[A-Z]/.test(s) && !s.includes("_")) return s;
  return toTitleLabel(s);
}
